// @flow
import React, { forwardRef, type Node } from 'react';
import classnames from 'classnames';
import { noop } from 'lodash';

import { useTracking } from 'Tracking/tracking-api/useTracking';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';

import { themes } from './TabHeader.themes';

export type TabHeaderProps = {
  children: Node,
  className?: string,
  collapseIconName?: IconName,
  color?: Colors,
  controlsId?: string,
  dataTestId?: string,
  expandIconName?: IconName,
  iconName?: IconName,
  id?: string,
  isAccordion?: boolean,
  isActive?: boolean,
  isDisabled?: boolean,
  onClick?: () => void,
  onKeyDown?: () => void,
  role?: string,
  style?: *,
  tabIndex?: number,
  theme?: string,
  trackingData?: ?TrackingData,
};

// $FlowFixMe
export const TabHeader = forwardRef((props: TabHeaderProps, ref) => {
  const {
    children,
    className,
    collapseIconName,
    controlsId,
    dataTestId = 'tab-header',
    expandIconName,
    iconName,
    id,
    isAccordion,
    isActive,
    isDisabled,
    onClick,
    onKeyDown,
    role,
    style,
    tabIndex,
    theme = 'default',
    trackingData,
  } = props;

  const track = !isActive && trackingData ? useTracking(trackingData) : noop;

  const {
    thBody,
    thChildren,
    thIcon,
    thIconWidth,
    thRoot,
    thToggleIcon,
    thToggleIconWidth,
  } = themes[theme](props);

  const toggleIcon = isActive ? collapseIconName : expandIconName;

  const aria = {
    // $FlowFixMe
    ...(controlsId ? { 'aria-controls': controlsId } : null),
    ...(isAccordion ? { 'aria-expanded': isActive } : { 'aria-selected': isActive }),
  };

  return (
    <button
      {...aria}
      className={classnames(className, 'reset-button', thRoot)}
      data-testid={dataTestId}
      disabled={isDisabled}
      onClick={() => {
        onClick && onClick();
        track({});
      }}
      onKeyDown={onKeyDown}
      data-cs-override-id="TabHeader" // ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id
      id={id}
      ref={ref}
      role={role}
      style={style}
      tabIndex={tabIndex}
      type="button"
    >
      <div className={thBody}>
        <div className={thChildren}>
          {!!iconName && <Icon className={thIcon} name={iconName} width={thIconWidth} />}
          {children}
        </div>
        {!!toggleIcon && (
          <Icon className={thToggleIcon} name={toggleIcon} width={thToggleIconWidth} />
        )}
      </div>
    </button>
  );
});
